import ReactMarkdown from "react-markdown";
import remarkMath from "remark-math";
import remarkGfm from "remark-gfm";
import rehypeKatex from "rehype-katex";
import { Lock, Globe2, ThumbsUp, ThumbsDown, HelpCircle, Check, MoreHorizontal } from "lucide-react";
import { FileCard } from "@/components/FileCard";
import { DocumentCard } from "@/components/DocumentCard";
import { VideoCard, seekVideo } from "@/components/VideoCard";
import { useState } from "react";
import { ListenButton, DrawButton, Diagrams, FlashcardButton } from "@/components/MessageTools";
import { ThinkingMascot } from "@/components/Mascot";

const stripDocTag = (s) => s.replace(/\[\[(?:document|draw)[^\]]*\]\]/g, "").replace(/\[\[[^\]]*$/, "").trimEnd();
const fixMath = (s) => (s || "").replace(/\\\[([\s\S]*?)\\\]/g, "$$$$$1$$$$").replace(/\\\(([\s\S]*?)\\\)/g, "$$$1$$");

const REMARK = [remarkMath, remarkGfm];
const REHYPE = [rehypeKatex];
const MD_COMPONENTS = { a: (p) => <a {...p} target="_blank" rel="noreferrer" /> };

const linkTimestamps = (s) => s.replace(/\[(\d{1,2}:\d{2}(?::\d{2})?)\](?!\()/g, "[$1](#vt-$1)");
const videoComponents = (videoId) => ({
  a: ({ href, children, ...p }) => (href?.startsWith("#vt-")
    ? <button type="button" onClick={() => seekVideo(videoId, href.slice(4))} className="font-mono text-sky-300 hover:underline" data-testid={`video-ts-${href.slice(4)}`}>{children}</button>
    : <a href={href} {...p} target="_blank" rel="noreferrer">{children}</a>),
});

export const Markdown = ({ children, videoId }) => (
  <div className="prose-tutor">
    <ReactMarkdown remarkPlugins={REMARK} rehypePlugins={REHYPE} components={videoId ? videoComponents(videoId) : MD_COMPONENTS}>
      {videoId ? linkTimestamps(fixMath(children)) : fixMath(children)}
    </ReactMarkdown>
  </div>
);

const Pill = ({ icon: Icon, cls, children, testid, title }) => (
  <span title={title} data-testid={testid} className={`inline-flex items-center gap-1.5 text-[11px] px-2.5 py-1 rounded-full border ${cls}`}>
    <Icon className="h-3 w-3" /> {children}
  </span>
);

export function MemoryBadges({ meta }) {
  if (!meta) return null;
  const { private_count, web_sources = [] } = meta;
  if (!private_count && !web_sources.length) return null;
  return (
    <div className="flex flex-wrap gap-1.5 mb-3" data-testid="memory-badges">
      {private_count > 0 && (
        <Pill icon={Lock} testid="badge-private-memory" cls="text-emerald-300 border-emerald-300/30 bg-emerald-500/10">Private memory · {private_count}</Pill>
      )}
      {web_sources.length > 0 && (
        <Pill icon={Globe2} testid="badge-web" cls="text-sky-300 border-sky-300/30 bg-sky-500/10">Web · {web_sources.length} sources</Pill>
      )}
    </div>
  );
}

function Sources({ sources }) {
  if (!sources?.length) return null;
  return (
    <div className="mt-4 border-t border-white/10 pt-3" data-testid="web-sources">
      <p className="text-[11px] uppercase tracking-[0.16em] text-neutral-500 mb-2">Sources</p>
      <ol className="space-y-1">
        {sources.map((s, i) => (
          <li key={s.url} className="text-xs text-neutral-400 truncate">
            <span className="text-sky-400 mr-1.5">[{i + 1}]</span>
            <a href={s.url} target="_blank" rel="noreferrer" className="hover:text-white underline-offset-2 hover:underline">{s.title}</a>
          </li>
        ))}
      </ol>
    </div>
  );
}

const FB = ({ onClick, active, icon: Icon, label, testid, hover }) => (
  <button onClick={onClick} data-testid={testid}
    className={`inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full border transition-colors ${active ? "border-white/30 bg-white/10 text-white" : `border-white/10 text-neutral-400 ${hover}`}`}>
    {active ? <Check className="h-3 w-3" /> : <Icon className="h-3 w-3" />} {label}
  </button>
);

function AnswerNotes({ meta }) {
  return (
    <>
      {meta?.auto_confused && (
        <p className="mt-3 text-xs text-yellow-200/80" data-testid="auto-confused-note">You seemed stuck, so I explained it more simply.</p>
      )}
      {meta?.web_locked && (
        <p className="mt-3 text-xs text-neutral-500 flex items-center gap-1.5" data-testid="web-locked-note">
          <Globe2 className="h-3 w-3 text-sky-400" />
          <span>A web search could have helped here. Web answers come with <a href="/app/billing" className="text-sky-400 hover:underline">Plus</a>.</span>
        </p>
      )}
    </>
  );
}

function AnswerActions({ msg, disabled, onFeedback, onConfused, onDrawn }) {
  return (
    <div className="flex flex-wrap gap-2 mt-4 [&>*]:shrink-0" data-testid={`answer-actions-${msg.message_id}`}>
      <FB testid={`feedback-helpful-${msg.message_id}`} icon={ThumbsUp} label="Helpful" active={msg.feedback === "helpful"}
        hover="hover:bg-emerald-500/10 hover:text-emerald-300" onClick={() => !disabled && msg.feedback !== "helpful" && onFeedback(msg, "helpful")} />
      <FB testid={`feedback-wrong-${msg.message_id}`} icon={ThumbsDown} label="Wrong" active={msg.feedback === "wrong"}
        hover="hover:bg-red-500/10 hover:text-red-300" onClick={() => !disabled && msg.feedback !== "wrong" && onFeedback(msg, "wrong")} />
      <FB testid={`feedback-confused-${msg.message_id}`} icon={HelpCircle} label="I'm confused"
        hover="hover:bg-yellow-400/10 hover:text-yellow-200" onClick={() => !disabled && onConfused()} />
      <span className="w-px bg-white/10 mx-1" />
      <ListenButton messageId={msg.message_id} cached={!!msg.speech_path || !!msg.voice_turn} />
      <MoreTools msg={msg} onDrawn={onDrawn} />
    </div>
  );
}

// Secondary tools stay mounted (so a running Stop keeps working) and are only shown when "More" is open.
function MoreTools({ msg, onDrawn }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button onClick={() => setOpen((v) => !v)} aria-expanded={open} data-testid={`more-tools-${msg.message_id}`}
        className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full border border-white/10 text-neutral-400 hover:bg-white/5 hover:text-white">
        <MoreHorizontal className="h-3 w-3" /> {open ? "Less" : "More"}
      </button>
      <span className={open ? "contents" : "hidden"} data-testid={`more-tools-panel-${msg.message_id}`}>
        <DrawButton msg={msg} onDrawn={onDrawn} />
        <FlashcardButton msg={msg} />
      </span>
    </>
  );
}

function StreamingAnswer({ msg }) {
  return (
    <div className="rise" data-testid="assistant-message-streaming">
      <MemoryBadges meta={msg.meta} />
      {msg.content
        ? <div className="caret"><Markdown>{stripDocTag(msg.content)}</Markdown></div>
        : <ThinkingMascot />}
      <Sources sources={msg.meta?.web_sources} />
    </div>
  );
}

function SavedAnswer({ msg, onFeedback, onConfused, disabled }) {
  const [diagrams, setDiagrams] = useState(msg.diagram_ids || []);
  return (
    <div className="rise" data-testid={`assistant-message-${msg.message_id}`}>
      <MemoryBadges meta={msg.meta} />
      <div>{msg.content && <Markdown videoId={msg.meta?.video_id}>{stripDocTag(msg.content)}</Markdown>}</div>
      <Sources sources={msg.meta?.web_sources} />
      {(msg.document_ids || []).map((id) => <DocumentCard key={id} id={id} />)}
      <Diagrams ids={diagrams} />
      <AnswerNotes meta={msg.meta} />
      {msg.message_id && <AnswerActions msg={msg} disabled={disabled} onFeedback={onFeedback} onConfused={onConfused}
        onDrawn={(id) => setDiagrams((d) => [...d, id])} />}
    </div>
  );
}

export function AssistantMessage({ msg, streaming, ...rest }) {
  return streaming ? <StreamingAnswer msg={msg} /> : <SavedAnswer msg={msg} {...rest} />;
}

const MODE_LABEL = { confused: "I'm confused", retry: "That was wrong" };

export function UserMessage({ msg, onVideoDone }) {
  return (
    <div className="flex justify-end rise" data-testid="user-message">
      <div className="max-w-[85%] space-y-2 flex flex-col items-end">
        {msg.video_id && <VideoCard id={msg.video_id} onDone={onVideoDone} />}
        {msg.files?.length > 0 && <div className="flex flex-wrap gap-2 justify-end">{msg.files.map((f) => <FileCard key={f.file_id} file={f} />)}</div>}
        {(msg.content || MODE_LABEL[msg.mode]) && (
          <div className="bg-white/5 border border-white/10 rounded-2xl px-4 py-2.5 text-[15px] whitespace-pre-wrap">
            {MODE_LABEL[msg.mode] && <span className="block text-[11px] uppercase tracking-wider text-yellow-200/80 mb-1">{MODE_LABEL[msg.mode]}</span>}
            {msg.content}
          </div>
        )}
      </div>
    </div>
  );
}
